import { useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { isValidString } from "../../../Utils/validators";
import { useAppDispatch } from "../../../Hooks/reduxHook";
import { addUser, editUser, listUser } from "../../../Features/slice/userSlice";
import { formatAsUsPhoneNo } from "Utils/transform";

const initialValues = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  role_name: "",
  phone: "",
  username: "",
  isEditForm: false,
  is_active: 1
};

const signupSchema = Yup.object().shape({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  email: Yup.string().email("Enter valid email").required("Email is required"),
  password: Yup.string().
    trim()
    .when("isEditForm", {
      is: false,
      then: (schema) => Yup.string()
        .required("Enter valid password")
        .matches(/^[a-zA-Z0-9]+$/, 'Password should be alphanumeric').min(6).max(12),
      otherwise: (schema) => Yup.string(),
    }),
  username: Yup.string().required("Username is required"),
  phone: Yup.string()
    .required("Phone number is required")
    // .min(13, 'Enter valid phone number')
    .max(13, 'Enter valid phone number'),
  // .matches(/[(0-9){3}[0-9]{3}-[0-9]{4}]/g, 'Enter valid phone number'),
  role_name: Yup.string().required("Select role"),
  isEditForm: Yup.boolean(),
  is_active: Yup.number(),
});

export default function AddUser({
  callback,
  selectedUser,
  selectedUserId,
}: {
  callback: (response: boolean, message: string) => void;
  selectedUser: typeof initialValues;
  selectedUserId?: number;
}) {

  const signupForm = useFormik({
    initialValues: selectedUser.isEditForm ? selectedUser : initialValues,
    validationSchema: signupSchema,
    validateOnMount: false,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values: typeof initialValues) => { },
  });

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = (e: any) => {
    e.preventDefault();
    setErrorMsg('');
    signupForm
      .submitForm()
      .then(() => {
        if (signupForm.isValid) {
          setIsLoading(true);
          if (selectedUser.isEditForm) {
            const is_active = signupForm.values.is_active ? 1 : 0;
            const payload = {
              user_id: selectedUserId as number,
              first_name: signupForm.values.first_name,
              last_name: signupForm.values.last_name,
              username: signupForm.values.username,
              email: signupForm.values.email,
              phone: signupForm.values.phone,
              role_name: signupForm.values.role_name,
              is_active
            };
            dispatch(
              editUser({
                payload,
                successCB: (response: any) => {
                  dispatch(listUser());
                  signupForm.resetForm();
                  callback(true, "User Updated Successfully!");
                  setIsLoading(false);
                },
                failureCB: (error: any) => {
                  setIsLoading(false);
                  const message = error?.response?.data?.message || "Something went wrong";
                  setErrorMsg(message);
                },
              })
            );
          } else {
            const phone = isValidString(signupForm.values.phone) ? (signupForm.values.phone)?.replace(/[()-]/g, '') : signupForm.values.phone;
            const payload = {
              first_name: signupForm.values.first_name,
              last_name: signupForm.values.last_name,
              email: signupForm.values.email,
              role_name: signupForm.values.role_name,
              password: signupForm.values.password,
              username: signupForm.values.username,
              phone,
              is_active: signupForm.values.is_active,
            };
            dispatch(
              addUser({
                payload,
                successCB: (response: any) => {
                  dispatch(listUser());
                  signupForm.resetForm();
                  setIsLoading(false);
                  callback(true, "User Added Successfully!");
                },
                failureCB: (error: any) => {
                  setIsLoading(false);
                  const message = error?.response?.data?.message || "Something went wrong";
                  setErrorMsg(message);
                },
              })
            );
          }
        } else {
          setErrorMsg("Please fix the error and try again");
        }
      })
      .catch((error) => {
        setErrorMsg("Check your form info");
      });
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            autoComplete="given-name"
            name="firstName"
            required
            fullWidth
            id="firstName"
            label="First Name"
            autoFocus
            value={signupForm.values.first_name}
            onChange={(e) =>
              signupForm.setFieldValue("first_name", e.target.value)
            }
            onBlur={signupForm.handleBlur("first_name")}
          />
          {isValidString(signupForm.errors.first_name) &&
            signupForm.touched.first_name && (
              <FormHelperText error>
                {signupForm.errors.first_name}
              </FormHelperText>
            )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="lastName"
            label="Last Name"
            name="lastName"
            autoComplete="family-name"
            value={signupForm.values.last_name}
            onChange={(e) =>
              signupForm.setFieldValue("last_name", e.target.value)
            }
            onBlur={signupForm.handleBlur("last_name")}
          />
          {isValidString(signupForm.errors.last_name) &&
            signupForm.touched.last_name && (
              <FormHelperText error>
                {signupForm.errors.last_name}
              </FormHelperText>
            )}
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={signupForm.values.email}
            onChange={(e) => signupForm.setFieldValue("email", e.target.value)}
            onBlur={signupForm.handleBlur("email")}
            disabled={selectedUser.isEditForm}
          />
          {isValidString(signupForm.errors.email) &&
            signupForm.touched.email && (
              <FormHelperText error>{signupForm.errors.email}</FormHelperText>
            )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            autoComplete="given-name"
            name="phone"
            required
            fullWidth
            id="phone"
            label="Phone"
            autoFocus
            value={formatAsUsPhoneNo(parseInt(signupForm.values.phone)) || signupForm.values.phone}
            onChange={(e) => {
              signupForm.setFieldValue("phone", e.target.value);
            }
            }
            inputProps={{
              maxLength: 13
            }}
            onBlur={signupForm.handleBlur("phone")}
          />
          {isValidString(signupForm.errors.phone) &&
            signupForm.touched.phone && (
              <FormHelperText error>
                {signupForm.errors.phone}
              </FormHelperText>
            )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            autoComplete="given-name"
            name="userName"
            required
            fullWidth
            id="userName"
            label="User Name"
            autoFocus
            value={signupForm.values.username}
            onChange={(e) =>
              signupForm.setFieldValue("username", e.target.value)
            }
            onBlur={signupForm.handleBlur("username")}
            disabled={selectedUser.isEditForm}
          />
          {isValidString(signupForm.errors.username) &&
            signupForm.touched.username && (
              <FormHelperText error>
                {signupForm.errors.username}
              </FormHelperText>
            )}
        </Grid>
        {!selectedUser.isEditForm ? (
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={signupForm.values.password}
              onChange={(e) =>
                signupForm.setFieldValue("password", e.target.value)
              }
              onBlur={signupForm.handleBlur("password")}
            />

            {isValidString(signupForm.errors.password) &&
              signupForm.touched.password && (
                <FormHelperText error>
                  {signupForm.errors.password}
                </FormHelperText>
              )}
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Role</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={signupForm.values.role_name}
              label="Age"
              onChange={(event) =>
                signupForm.setFieldValue("role_name", event.target.value)
              }
            >
              <MenuItem value={"ADMIN"}>Admin</MenuItem>
              <MenuItem value={"EMPLOYEE"}>Employee</MenuItem>
              <MenuItem value={"MANAGER"}>Manager</MenuItem>
            </Select>
          </FormControl>
          {isValidString(signupForm.errors.role_name) && signupForm.touched.role_name && (
            <FormHelperText error>{signupForm.errors.role_name}</FormHelperText>
          )}
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormControlLabel
              control={<Checkbox checked={signupForm.values.is_active == 1} />} label={'Active'} onChange={() => {
                signupForm.setFieldValue('is_active', signupForm.values.is_active === 1 ? 0 : 1)
              }} />
          </FormControl>
        </Grid>
      </Grid>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        onClick={handleSubmit}
      >
        {isLoading
          ? "Please wait"
          : `${selectedUser.isEditForm ? "Edit" : "Add"} User`}
      </Button>
      {isValidString(errorMsg) ? <Typography
        variant="body1" component={'body'} color={'red'}
      >
        {errorMsg}
      </Typography> : null}
    </Box>
  );
}
